import React, { useMemo, useRef, useState, useEffect } from 'react'
import Swal from 'sweetalert2'
import { getUserInfoSync } from '../../config/api'
import apiClient from '../../config/api'
import { receiptsDB } from '../../db/indexedDB'
import {
  printViaLocalService,
  formatReceiptAsPlainText,
  splitBranchAddressForReceipt,
  formatReceiptPhoneLine,
} from '../../utils/localPrintService'
import { fetchAndStoreExchangeRate, getExchangeRate, isZigPayment, formatPaymentMethod } from '../../utils/exchangeRate'

const brandColors = {
  primary: '#2ca01c',
  dark: '#1f6f14',
  accent: '#f4fbf2',
  slate: '#2f3b44',
}

// Helper function to get branch_id from userInfo
const getBranchId = (userInfo) => {
  if (!userInfo) {
    return null
  }

  if (typeof userInfo.branch === 'number') {
    return userInfo.branch
  }

  if (typeof userInfo.branch === 'object' && userInfo.branch !== null) {
    if (typeof userInfo.branch.id === 'number') {
      return userInfo.branch.id
    }
  }

  return (
    userInfo.branch_id ||
    userInfo.assigned_branch_id ||
    userInfo.branchId ||
    null
  )
}

const Checkout = ({ onNavigate }) => {
  const [draft] = useState(() => {
    try {
      const stored = sessionStorage.getItem('cashierCheckoutDraft')
      return stored ? JSON.parse(stored) : null
    } catch (error) {
      console.error('Unable to read checkout draft:', error)
      return null
    }
  })

  const userInfo = useMemo(() => {
    // Try sync first, then async
    return getUserInfoSync() || null
  }, [])
  const branchName = useMemo(() => {
    return userInfo?.branch_name || 'Main Branch'
  }, [userInfo])

  const branchAddress = useMemo(() => {
    // Prefer explicit branch_address, then nested branch.address, then branch name as fallback
    return userInfo?.branch_address || userInfo?.branch?.address || branchName
  }, [userInfo, branchName])

  const branchPhone = useMemo(() => {
    const p = userInfo?.branch_phone
    if (p == null || p === '') return ''
    return String(p)
  }, [userInfo])

  const receiptHeaderPhoneLine = useMemo(() => formatReceiptPhoneLine(branchPhone), [branchPhone])

  const cashierName = useMemo(() => {
    return userInfo?.employee_name || 'Cashier'
  }, [userInfo])
  
  const cartItems = useMemo(() => draft?.cartItems ?? [], [draft])
  const subtotal = useMemo(() => draft?.subtotal ?? 0, [draft])
  // Use reference from draft if available, otherwise auto-generate a short DM code
  const receiptNumber = useMemo(() => {
    if (draft?.reference) return draft.reference

    const now = new Date()
    const day = String(now.getDate()).padStart(2, '0')
    const sec = String(now.getSeconds()).padStart(2, '0')
    const rand = String(Math.floor(Math.random() * 100)).padStart(2, '0')
    const shortCode = `${day}${sec}${rand}` // 6 digits
    return `DM-${shortCode}`
  }, [draft])
  const customerName = draft?.customer?.name ?? 'Walk-in customer'
  const [amountReceived, setAmountReceived] = useState('')
  const [paymentMethod, setPaymentMethod] = useState('usd_cash')
  const [amountError, setAmountError] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState('')
  const [applyDiscount, setApplyDiscount] = useState(false)
  const [availableDiscounts, setAvailableDiscounts] = useState([])
  const [selectedDiscount, setSelectedDiscount] = useState(null)
  const [loadingDiscounts, setLoadingDiscounts] = useState(false)
  const [calculatedDiscount, setCalculatedDiscount] = useState(0)
  const [exchangeRate, setExchangeRate] = useState(null)
  const hasCartItems = cartItems.length > 0

  // Load exchange rate on mount
  useEffect(() => {
    const loadExchangeRate = async () => {
      try {
        const rate = await getExchangeRate()
        setExchangeRate(rate)
        
        // If no cached rate and online, try to fetch
        if (!rate && navigator.onLine) {
          const fetchedRate = await fetchAndStoreExchangeRate()
          setExchangeRate(fetchedRate)
        }
      } catch (error) {
        console.error('Error loading exchange rate:', error)
      }
    }
    
    loadExchangeRate()
    
    // Listen for exchange rate updates from the global network listener
    const handleExchangeRateUpdate = (event) => {
      const { rate } = event.detail
      if (rate) {
        console.log('[CHECKOUT] Exchange rate updated via global listener:', rate)
        setExchangeRate(rate)
      }
    }
    
    window.addEventListener('exchangeRateUpdated', handleExchangeRateUpdate)
    
    return () => {
      window.removeEventListener('exchangeRateUpdated', handleExchangeRateUpdate)
    }
  }, [])

  // Calculate total - convert to ZIG if payment is in ZIG
  // Note: This will recalculate when exchangeRate state updates
  const total = useMemo(() => {
    const baseTotal = Math.max(0, subtotal - calculatedDiscount)
    
    // If payment is in ZIG and we have exchange rate, convert to ZIG
    if (isZigPayment(paymentMethod) && exchangeRate) {
      return baseTotal * exchangeRate
    }
    
    return baseTotal
  }, [subtotal, calculatedDiscount, paymentMethod, exchangeRate])

  // Store a ref to the current exchange rate for use in handleConfirmSale
  // This allows us to use the loaded rate even if state hasn't updated yet
  const exchangeRateRef = React.useRef(exchangeRate)
  React.useEffect(() => {
    exchangeRateRef.current = exchangeRate
  }, [exchangeRate])

  // Calculate change due - handle ZIG conversion
  const changeDue = useMemo(() => {
    const amount = parseFloat(amountReceived) || 0
    return Math.max(amount - total, 0)
  }, [amountReceived, total])

  // Helpers for receipt preview currency display
  const isZig = isZigPayment(paymentMethod)
  const currencySymbol = isZig ? 'ZIG' : 'USD'
  const convertForDisplay = (amount) => {
    const numericAmount = parseFloat(amount) || 0
    return isZig && exchangeRate ? numericAmount * exchangeRate : numericAmount
  }

  const receiptRef = useRef(null)

  // Fetch available discounts when applyDiscount is checked
  useEffect(() => {
    const fetchDiscounts = async () => {
      if (!applyDiscount) {
        setAvailableDiscounts([])
        setSelectedDiscount(null)
        setCalculatedDiscount(0)
        return
      }

      const branchId = getBranchId(userInfo)
      if (!branchId) return

      setLoadingDiscounts(true)
      try {
        const response = await apiClient.get('/api/sales/discounts/available/', {
          params: {
            branch_id: branchId,
            is_active: true
          }
        })
        setAvailableDiscounts(response.data || [])
      } catch (error) {
        console.error('Error fetching discounts:', error)
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'Failed to load discounts. Please try again.',
          timer: 2000,
          showConfirmButton: false
        })
      } finally {
        setLoadingDiscounts(false)
      }
    }

    fetchDiscounts()
  }, [applyDiscount, userInfo])

  // Calculate discount when selectedDiscount or cartItems change
  useEffect(() => {
    if (!selectedDiscount || !cartItems.length) {
      setCalculatedDiscount(0)
      return
    }

    const calculateDiscount = () => {
      let discountAmount = 0
      const discount = selectedDiscount

      // Check if discount is valid
      if (!discount.is_valid) {
        setCalculatedDiscount(0)
        return
      }

      // Check date validity
      const now = new Date()
      if (discount.start_date && new Date(discount.start_date) > now) {
        setCalculatedDiscount(0)
        return
      }
      if (discount.end_date && new Date(discount.end_date) < now) {
        setCalculatedDiscount(0)
        return
      }

      // Check usage limit
      if (discount.usage_limit && discount.usage_count >= discount.usage_limit) {
        setCalculatedDiscount(0)
        return
      }

      // Calculate based on apply_to
      if (discount.apply_to === 'product' && discount.product) {
        // Apply to specific product items
        cartItems.forEach(item => {
          const productId = item.product_id ?? item.id
          if (productId === discount.product) {
            const itemTotal = item.price * item.quantity
            if (discount.discount_type === 'percentage') {
              let itemDiscount = itemTotal * (parseFloat(discount.discount_value) / 100)
              if (discount.max_discount_amount) {
                itemDiscount = Math.min(itemDiscount, parseFloat(discount.max_discount_amount))
              }
              discountAmount += itemDiscount
            } else {
              // fixed
              discountAmount += parseFloat(discount.discount_value) * item.quantity
            }
          }
        })
      } else if (discount.apply_to === 'category' && discount.category) {
        // Apply to category items (we don't have category info in cartItems, so skip for now)
        // This would require category info in cartItems
      } else if (discount.apply_to === 'min_purchase' && discount.min_purchase_amount) {
        // Check if subtotal meets minimum
        if (subtotal >= parseFloat(discount.min_purchase_amount)) {
          if (discount.discount_type === 'percentage') {
            discountAmount = subtotal * (parseFloat(discount.discount_value) / 100)
            if (discount.max_discount_amount) {
              discountAmount = Math.min(discountAmount, parseFloat(discount.max_discount_amount))
            }
          } else {
            // fixed
            discountAmount = parseFloat(discount.discount_value)
          }
        }
      } else {
        // Apply to entire sale (all, branch)
        if (discount.discount_type === 'percentage') {
          discountAmount = subtotal * (parseFloat(discount.discount_value) / 100)
          if (discount.max_discount_amount) {
            discountAmount = Math.min(discountAmount, parseFloat(discount.max_discount_amount))
          }
        } else {
          // fixed
          discountAmount = parseFloat(discount.discount_value)
        }
      }

      setCalculatedDiscount(Math.max(0, Math.round(discountAmount * 100) / 100))
    }

    calculateDiscount()
  }, [selectedDiscount, cartItems, subtotal])

  const handleBackToSales = () => {
    if (typeof onNavigate === 'function') {
      onNavigate('new-sale')
    }
  }

  /**
   * Prints the receipt as plain text via Local Print Service
   * Formats receipt data as plain text and sends to print service
   * Uses Plain Text printing for best compatibility
   * Falls back to browser print if service is unavailable
   */
  const handlePrintReceipt = async () => {
    if (!hasCartItems) {
      console.error('[PRINT] No items to print')
      return
    }

    // Prepare receipt data for plain text printing
    const isZig = isZigPayment(paymentMethod)
    const convertToReceiptCurrency = (amount) => {
      const numericAmount = parseFloat(amount) || 0
      return isZig && exchangeRate ? numericAmount * exchangeRate : numericAmount
    }

    const receiptCartItems = cartItems.map((item) => {
      const unitPriceBase = parseFloat(item.price) || 0
      const quantity = Number(item.quantity) || 0
      const unitPrice = convertToReceiptCurrency(unitPriceBase)
      const lineTotal = convertToReceiptCurrency(unitPriceBase * quantity)

      return {
        ...item,
        price: unitPrice,
        subtotal: lineTotal,
        currency: isZig ? 'ZIG' : 'USD',
      }
    })

    const receiptSubtotal = convertToReceiptCurrency(subtotal)
    const receiptDiscount = convertToReceiptCurrency(calculatedDiscount)
    const receiptTotal =
      isZig && exchangeRate
        ? Math.max(0, subtotal - calculatedDiscount) * exchangeRate
        : total
    const usdEquivalent = null

    const receiptData = {
      branchName: branchName,
      branchAddress: branchAddress,
      cashierName: cashierName,
      receiptNumber: receiptNumber,
      customerName: customerName,
      cartItems: receiptCartItems,
      subtotal: receiptSubtotal,
      discount: receiptDiscount,
      discountName: selectedDiscount?.name || null,
      total: receiptTotal,
      paymentMethod: paymentMethod,
      paymentMethodFormatted: formatPaymentMethod(paymentMethod),
      amountReceived: parseFloat(amountReceived) || 0,
      changeDue: changeDue,
      isZigPayment: isZig,
      exchangeRate: exchangeRate,
      usdEquivalent,
      companyName: 'Decor Masters',
      companyPhone: branchPhone,
    }

    try {
      // Format receipt as plain text and send to print service
      console.log('[PRINT] Formatting receipt as plain text and sending to Local Print Service...')
      const result = await printViaLocalService(receiptData)

      if (result.success) {
        console.log('[PRINT] ✅ Successfully printed via Local Print Service')
        return
      } else {
        // Service returned but with error, fall back to browser print
        console.warn('[PRINT] ⚠️ Local Print Service returned error:', result.error, '- falling back to browser print')
        handleBrowserPrintFallback(receiptData)
      }
    } catch (error) {
      // If service is unavailable, fall back to browser print
      if (error.message === 'PRINT_SERVICE_UNAVAILABLE') {
        console.log('[PRINT] ⚠️ Local Print Service not available, falling back to browser print')
        handleBrowserPrintFallback(receiptData)
      } else {
        // Show user-friendly error message for printer issues
        const errorMsg = error.message || 'Unknown error'
        console.warn('[PRINT] ⚠️ Print error:', errorMsg, '- falling back to browser print')
        
        // If it's a printer rejection error, show a brief notification
        if (errorMsg.includes('not accepting') || errorMsg.includes('rejected') || errorMsg.includes('Printer')) {
          Swal.fire({
            icon: 'warning',
            title: 'Auto-print Failed',
            text: 'Printer rejected the job. Opening print dialog instead.',
            timer: 3000,
            showConfirmButton: false,
            toast: true,
            position: 'top-end'
          })
        }
        
        // Fall back to browser print
        handleBrowserPrintFallback(receiptData)
      }
    }
  }

  /**
   * Fallback to browser's native print dialog
   * Opens a new window with just the receipt content and prints it
   * Uses the exact same format as the printer service (plain text)
   */
  const handleBrowserPrintFallback = (receiptData) => {
    try {
      if (!receiptData) {
        console.error('[PRINT] No receipt data provided for browser print fallback')
        // Fallback to printing current page if no receipt data
        window.print()
        return
      }

      // Format receipt as plain text (same format as printer service)
      const plainText = formatReceiptAsPlainText(receiptData)
      
      // Create HTML document with the plain text receipt in a <pre> tag
      // This preserves the exact formatting and spacing
      const receiptHTML = `<!DOCTYPE html>
<html>
  <head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>Receipt</title>
    <style>
      * {
        margin: 0;
        padding: 0;
        box-sizing: border-box;
      }
      html, body {
        width: 100%;
        height: 100%;
        font-family: 'Courier New', Courier, monospace;
        font-size: 12px;
        line-height: 1.4;
        color: #000000;
        background: #ffffff;
      }
      body {
        padding: 20px;
        display: flex;
        justify-content: center;
        align-items: flex-start;
      }
      pre {
        white-space: pre;
        font-family: 'Courier New', Courier, monospace;
        font-size: 12px;
        line-height: 1.4;
        margin: 0;
        padding: 0;
        overflow: visible;
      }
      @media print {
        body {
          padding: 10px;
        }
        pre {
          font-size: 11px;
        }
      }
    </style>
  </head>
  <body>
    <pre>${plainText.replace(/</g, '&lt;').replace(/>/g, '&gt;')}</pre>
  </body>
</html>`
      
      // Create a new window with just the receipt
      const printWindow = window.open('', '_blank', 'width=800,height=600')
      if (!printWindow) {
        throw new Error('Popup blocked. Please allow popups for this site to print receipts.')
      }
      
      printWindow.document.write(receiptHTML)
      printWindow.document.close()
      
      // Function to trigger print and handle window closing
      const triggerPrint = () => {
        setTimeout(() => {
          printWindow.print()
          // Close the window after a delay to allow print dialog to appear
          // User can still cancel the print dialog if needed
          setTimeout(() => {
            printWindow.close()
          }, 1000)
        }, 500)
      }
      
      // Wait for content to load, then print
      printWindow.onload = triggerPrint
      
      // If onload doesn't fire (content already loaded), print immediately
      if (printWindow.document.readyState === 'complete') {
        triggerPrint()
      } else {
        // Fallback: wait a bit and then try to print
        setTimeout(triggerPrint, 1000)
      }
    } catch (error) {
      console.error('[PRINT] Browser print failed:', error)
      Swal.fire({
        icon: 'error',
        title: 'Print Failed',
        text: error.message || 'Unable to open print dialog. Please try printing manually.',
        confirmButtonText: 'OK'
      })
    }
  }

  const handleConfirmSale = async () => {
    if (!cartItems.length) return

    // Validate exchange rate for ZIG payments
    // Check both state and cached value (for offline support)
    let rateToUse = exchangeRate || exchangeRateRef.current
    if (isZigPayment(paymentMethod) && !rateToUse) {
      // Try to get from cache if not in state (offline scenario)
      try {
        rateToUse = await getExchangeRate()
        if (rateToUse) {
          // Update state with cached value - this will trigger total recalculation
          setExchangeRate(rateToUse)
          // Also update ref immediately
          exchangeRateRef.current = rateToUse
        }
      } catch (error) {
        console.error('Error getting cached exchange rate:', error)
      }
    }

    if (isZigPayment(paymentMethod) && !rateToUse) {
      await Swal.fire({
        icon: 'error',
        title: 'Exchange Rate Required',
        text: 'Exchange rate is not available. Please ensure you have logged in while online to cache the exchange rate, or check your connection and try again.',
        confirmButtonText: 'OK'
      })
      return
    }

    // Recalculate total if we just loaded the rate and payment is ZIG
    // This ensures the total is correct before validation
    const baseSubtotal = Math.max(0, subtotal - calculatedDiscount)
    const isZig = isZigPayment(paymentMethod)
    const exchangeRateUsed = isZig ? rateToUse : null
    const finalTotal = isZig && exchangeRateUsed ? baseSubtotal * exchangeRateUsed : baseSubtotal

    const amount = parseFloat(amountReceived)

    if (Number.isNaN(amount)) {
      setAmountError('Enter the amount tendered to continue.')
      return
    }
    if (amount < finalTotal) {
      setAmountError('Amount received is less than the total due.')
      return
    }

    setAmountError('')
    setSubmitError('')
    setIsSubmitting(true)

    try {
      // Get branch_id from userInfo
      const branchId = getBranchId(userInfo)
      
      if (!branchId) {
        throw new Error('Branch ID is required. Please ensure you are assigned to a branch.')
      }

      // Prepare payload - matching Postman format exactly
      // Use product_id if available (from updated NewSale.js), otherwise fallback to id
      const payload = {
        branch_id: Number(branchId),
        type_of_payment: paymentMethod,
        items: cartItems.map(item => {
          const productId = item.product_id ?? item.id
          if (!productId) {
            throw new Error(`Product ID not found for item: ${item.name || 'Unknown'}`)
          }

          const unitPriceBase = parseFloat(item.price)
          const unitPrice =
            isZig && exchangeRateUsed && !Number.isNaN(unitPriceBase)
              ? unitPriceBase * exchangeRateUsed
              : unitPriceBase

          const itemPayload = {
            product_id: Number(productId),
            quantity: String(item.quantity)
          }

          // Include explicit unit_price so ZIG payments send converted amounts
          if (!Number.isNaN(unitPrice)) {
            itemPayload.unit_price = Number(unitPrice.toFixed(2))
          }

          return itemPayload
        })
      }

      // Add discount_code if discount is selected
      if (selectedDiscount && selectedDiscount.code) {
        payload.discount_code = selectedDiscount.code
      }
      
      console.log('=== SALE CONFIRMATION START ===')
      console.log('User Info:', userInfo)
      console.log('Branch ID:', branchId)
      console.log('Cart Items:', cartItems)
      console.log('Payload:', JSON.stringify(payload, null, 2))
      console.log('Total Amount:', finalTotal)
      console.log('Amount Received:', amountReceived)
      console.log('Payment Method:', paymentMethod)
      console.log('Online Status:', navigator.onLine)
      console.log('Exchange Rate Used:', exchangeRateUsed)

      // Generate sync_id for this sale (used for both online and offline)
      const sync_id = `sync-${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;
      console.log('Generated Sync ID:', sync_id)

      // Calculate change due with the correct total
      const calculatedChangeDue = Math.max((parseFloat(amountReceived) || 0) - finalTotal, 0)

      // Check if device is online
      if (!navigator.onLine) {
        // OFFLINE: Save directly to receiptsDB (will be synced via bulk endpoint later)
        console.log('[CHECKOUT] 📴 Device is offline - saving to receiptsDB for bulk sync')
        
        const discountCode = selectedDiscount?.code ? String(selectedDiscount.code).trim() : null;
        
        const receiptData = {
          sync_id: sync_id,
          branch_id: branchId,
          items: cartItems.map(item => ({
            product_id: item.product_id ?? item.id,
            quantity: item.quantity,
            name: item.name,
            price: item.price,
            unit_price: !Number.isNaN(parseFloat(item.price))
              ? (isZig && exchangeRateUsed ? parseFloat(item.price) * exchangeRateUsed : parseFloat(item.price))
              : null,
            currency: isZig ? 'ZIG' : 'USD',
          })),
          total: finalTotal,
          subtotal: subtotal,
          discount: calculatedDiscount,
          discount_code: discountCode || null,
          payment_method: paymentMethod,
          amount_received: parseFloat(amountReceived) || 0,
          change_due: calculatedChangeDue,
          exchange_rate_used: exchangeRateUsed || null,
          currency: isZig ? 'ZIG' : 'USD',
          customer: { name: customerName },
          cashier_name: cashierName,
          branch_name: branchName,
        }
        
        await receiptsDB.create(receiptData)
        console.log('[CHECKOUT] ✅ Receipt saved to receiptsDB with sync_id:', sync_id, {
          hasDiscount: !!selectedDiscount,
          discountCode: discountCode || 'none',
          discountAmount: calculatedDiscount,
          selectedDiscount: selectedDiscount ? {
            id: selectedDiscount.id,
            name: selectedDiscount.name,
            code: selectedDiscount.code
          } : null
        })
        
        // Clear draft and cart
        sessionStorage.removeItem('cashierCheckoutDraft')
        sessionStorage.removeItem('cashierCartItems')
        
        // Print the receipt
        handlePrintReceipt()
        
        // Show success message (offline mode)
        await Swal.fire({
          icon: 'success',
          title: 'Sale Queued!',
          text: 'The sale has been saved locally and will sync when you\'re back online. The receipt is being printed.',
          confirmButtonText: 'OK',
          confirmButtonColor: brandColors.primary,
          timer: 2500,
          timerProgressBar: true,
          showConfirmButton: false
        })
        
        // Navigate back to new sale
        if (typeof onNavigate === 'function') {
          onNavigate('new-sale')
        }
        
        console.log('[CHECKOUT] 📊 Receipt data queued for bulk sync:', {
          sync_id: sync_id,
          branch_id: branchId,
          items_count: cartItems.length,
          total: finalTotal
        })
        
        return
      }

      // ONLINE: Use individual API endpoint with sync_id in payload
      console.log('[CHECKOUT] 📡 Device is online - sending to /api/sales/ with sync_id')
      
      // Add sync_id to payload for online sale
      const payloadWithSyncId = {
        ...payload,
        sync_id: sync_id
      }
      
      console.log('[CHECKOUT] Payload with sync_id:', JSON.stringify(payloadWithSyncId, null, 2))
      
      try {
        // Make API call using apiClient directly (not offlineApi to avoid double handling)
        const apiClient = (await import('../../config/api')).default;
        const response = await apiClient.post('/api/sales/', payloadWithSyncId)
        
        console.log('=== SALE CONFIRMATION SUCCESS ===')
        console.log('API Response:', response)
        console.log('Response Data:', response.data)
        console.log('Sync ID sent:', sync_id)

        // Success - clear draft and cart
        sessionStorage.removeItem('cashierCheckoutDraft')
        sessionStorage.removeItem('cashierCartItems')
        console.log('Cleared checkout draft and cart from sessionStorage')
        
        // Print the receipt immediately
        console.log('Printing receipt...')
        handlePrintReceipt()
        
        // Show success message with SweetAlert
        await Swal.fire({
          icon: 'success',
          title: 'Sale Confirmed!',
          text: 'The sale has been successfully processed. The receipt is being printed.',
          confirmButtonText: 'OK',
          confirmButtonColor: brandColors.primary,
          timer: 2500,
          timerProgressBar: true,
          showConfirmButton: false
        })
        
        console.log('Success alert shown, navigating back to new sale')
        
        // Navigate back to new sale
        if (typeof onNavigate === 'function') {
          onNavigate('new-sale')
        }
      } catch (apiError) {
        // If online API call fails, fall back to saving to receiptsDB for bulk sync
        console.warn('[CHECKOUT] ⚠️ Online API call failed, falling back to offline storage:', apiError.message)
        
        const discountCode = selectedDiscount?.code ? String(selectedDiscount.code).trim() : null;
        
        const receiptData = {
          sync_id: sync_id,
          branch_id: branchId,
          items: cartItems.map(item => ({
            product_id: item.product_id ?? item.id,
            quantity: item.quantity,
            name: item.name,
            price: item.price,
            unit_price: !Number.isNaN(parseFloat(item.price))
              ? (isZig && exchangeRateUsed ? parseFloat(item.price) * exchangeRateUsed : parseFloat(item.price))
              : null,
            currency: isZig ? 'ZIG' : 'USD',
          })),
          total: finalTotal,
          subtotal: subtotal,
          discount: calculatedDiscount,
          discount_code: discountCode || null,
          payment_method: paymentMethod,
          amount_received: parseFloat(amountReceived) || 0,
          change_due: calculatedChangeDue,
          exchange_rate_used: exchangeRateUsed || null,
          currency: isZig ? 'ZIG' : 'USD',
          customer: { name: customerName },
          cashier_name: cashierName,
          branch_name: branchName,
        }
        
        await receiptsDB.create(receiptData)
        console.log('[CHECKOUT] ✅ Receipt saved to receiptsDB with sync_id (fallback):', sync_id, {
          hasDiscount: !!selectedDiscount,
          discountCode: discountCode || 'none',
          discountAmount: calculatedDiscount,
          selectedDiscount: selectedDiscount ? {
            id: selectedDiscount.id,
            name: selectedDiscount.name,
            code: selectedDiscount.code
          } : null
        })
        
        // Clear draft and cart
        sessionStorage.removeItem('cashierCheckoutDraft')
        sessionStorage.removeItem('cashierCartItems')
        
        // Print the receipt
        handlePrintReceipt()
        
        // Show success message (offline mode fallback)
        await Swal.fire({
          icon: 'success',
          title: 'Sale Queued!',
          text: 'The sale has been saved locally and will sync when connection is restored. The receipt is being printed.',
          confirmButtonText: 'OK',
          confirmButtonColor: brandColors.primary,
          timer: 2500,
          timerProgressBar: true,
          showConfirmButton: false
        })
        
        // Navigate back to new sale
        if (typeof onNavigate === 'function') {
          onNavigate('new-sale')
        }
        
        console.log('[CHECKOUT] 📊 Receipt data queued for bulk sync (fallback):', {
          sync_id: sync_id,
          branch_id: branchId,
          items_count: cartItems.length,
          total: total
        })
        
        return
      }
    } catch (error) {
      console.error('=== SALE CONFIRMATION FAILURE ===')
      console.error('Error Object:', error)
      console.error('Error Name:', error.name)
      console.error('Error Message:', error.message)
      console.error('Error Stack:', error.stack)
      console.error('Error Response:', error.response)
      console.error('Error Response Status:', error.response?.status)
      console.error('Error Response Status Text:', error.response?.statusText)
      console.error('Error Response Data:', error.response?.data)
      console.error('Error Response Headers:', error.response?.headers)
      console.error('Error Config:', error.config)
      console.error('Request URL:', error.config?.url)
      console.error('Request Method:', error.config?.method)
      console.error('Request Data:', error.config?.data)
      console.error('Request Headers:', error.config?.headers)
      
      // Log current state
      console.error('Current State at Error:')
      console.error('- Cart Items:', cartItems)
      console.error('- User Info:', userInfo)
      console.error('- Branch ID:', getBranchId(userInfo))
      console.error('- Total:', total)
      console.error('- Amount Received:', amountReceived)
      
      // Extract detailed error message
      let errorMessage = 'Failed to confirm sale. Please try again.'
      let errorDetails = null
      
      if (error.response?.data) {
        // Try to get detailed error information
        if (error.response.data.detail) {
          errorMessage = error.response.data.detail
        } else if (error.response.data.message) {
          errorMessage = error.response.data.message
        } else if (error.response.data.error) {
          errorMessage = error.response.data.error
        } else if (typeof error.response.data === 'string') {
          errorMessage = error.response.data
        } else if (error.response.data.non_field_errors) {
          errorMessage = Array.isArray(error.response.data.non_field_errors) 
            ? error.response.data.non_field_errors.join(', ')
            : error.response.data.non_field_errors
        } else {
          // Try to format field errors
          const fieldErrors = Object.entries(error.response.data)
            .map(([field, errors]) => {
              const errorText = Array.isArray(errors) ? errors.join(', ') : errors
              return `${field}: ${errorText}`
            })
            .join('; ')
          
          if (fieldErrors) {
            errorMessage = fieldErrors
          }
        }
        
        // Store full error data for debugging
        errorDetails = JSON.stringify(error.response.data, null, 2)
        console.error('Formatted Error Details:', errorDetails)
      } else if (error.message) {
        errorMessage = error.message
      }
      
      console.error('Final Error Message to Display:', errorMessage)
      
      // Show error with SweetAlert
      await Swal.fire({
        icon: 'error',
        title: 'Sale Failed',
        text: errorMessage,
        confirmButtonText: 'OK',
        confirmButtonColor: '#dc3545'
      })
      
      setSubmitError(errorMessage)
    } finally {
      setIsSubmitting(false)
    }
  }

  const formattedAmountReceived = useMemo(() => {
    return (parseFloat(amountReceived) || 0).toFixed(2)
  }, [amountReceived])

  return (
    <section className="section" style={{ background: '#f7f8fa', borderRadius: '24px', padding: '2rem' }}>
      <div className="row g-4 align-items-start">

        {/* PAYMENT INFORMATION */}
        <div className="col-12 col-lg-6">
          <div
            className="card border-0 shadow-sm h-100"
            style={{ borderRadius: '18px', borderTop: `4px solid ${brandColors.primary}` }}
          >
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <div>
                  <h6 className="text-uppercase text-muted mb-1" style={{ letterSpacing: '0.08em' }}>
                    Payment & Change
                  </h6>
                  <p className="mb-0 text-muted">Enter tendered amount and confirm the method.</p>
                </div>
              </div>

              <div className="mt-3">
                <label className="form-label fw-semibold">Amount Received</label>
                <input
                  type="number"
                  className="form-control"
                  value={amountReceived}
                  onChange={(e) => setAmountReceived(e.target.value)}
                  placeholder="Enter amount"
                />
                {amountError && <p className="text-danger small mt-1">{amountError}</p>}
                {submitError && <p className="text-danger small mt-1">{submitError}</p>}
              </div>

              <div className="mt-3">
                <label className="form-label fw-semibold">Payment Method</label>
                <select
                  className="form-select"
                  value={paymentMethod}
                  onChange={(e) => setPaymentMethod(e.target.value)}
                >
                  <option value="usd_cash">USD Cash</option>
                  <option value="zig_cash">ZIG Cash</option>
                  <option value="ecocash_usd">Ecocash USD</option>
                  <option value="ecocash_zig">Ecocash ZIG</option>
                  <option value="bank_transfer_zig">Bank Transfer ZIG</option>
                  <option value="bank_transfer_usd">Bank Transfer USD</option>
                </select>
                {isZigPayment(paymentMethod) && exchangeRate && (
                  <small className="text-muted d-block mt-1">
                    Exchange Rate: 1 USD = {exchangeRate.toFixed(6)} ZIG
                  </small>
                )}
                {isZigPayment(paymentMethod) && !exchangeRate && (
                  <small className="text-danger d-block mt-1">
                    Exchange rate not available. Please check your connection.
                  </small>
                )}
              </div>

              <div className="mt-3">
                <div className="form-check">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="applyDiscount"
                    checked={applyDiscount}
                    onChange={(e) => {
                      setApplyDiscount(e.target.checked)
                      if (!e.target.checked) {
                        setSelectedDiscount(null)
                        setCalculatedDiscount(0)
                      }
                    }}
                  />
                  <label className="form-check-label" htmlFor="applyDiscount">
                    Apply Discount
                  </label>
                </div>
              </div>

              {applyDiscount && (
                <div className="mt-3">
                  <label className="form-label fw-semibold">Select Discount</label>
                  {loadingDiscounts ? (
                    <div className="text-muted small">Loading discounts...</div>
                  ) : (
                    <select
                      className="form-select"
                      value={selectedDiscount?.id || ''}
                      onChange={(e) => {
                        const discountId = parseInt(e.target.value)
                        const discount = availableDiscounts.find(d => d.id === discountId)
                        setSelectedDiscount(discount || null)
                      }}
                    >
                      <option value="">Select a discount</option>
                      {availableDiscounts.map(discount => (
                        <option key={discount.id} value={discount.id}>
                          {discount.name} ({discount.code || 'No Code'}) - {discount.discount_type === 'percentage' 
                            ? `${discount.discount_value}%` 
                            : `$${discount.discount_value}`}
                        </option>
                      ))}
                    </select>
                  )}
                  {selectedDiscount && calculatedDiscount > 0 && (
                    <div className="mt-2 text-success small">
                      Discount: -${calculatedDiscount.toFixed(2)}
                    </div>
                  )}
                </div>
              )}

              <div className="mt-4">
                <p className="fw-semibold mb-1">Total Due:</p>
                <h3 className="fw-bold" style={{ color: brandColors.dark }}>
                  {isZigPayment(paymentMethod) ? 'ZIG' : 'USD'} {total.toFixed(2)}
                </h3>
                <p className="fw-semibold mt-3 mb-1">Change Due:</p>
                <h4 className="fw-bold text-success">
                  {isZigPayment(paymentMethod) ? 'ZIG' : 'USD'} {changeDue.toFixed(2)}
                </h4>
              </div>

              <div className="mt-4 d-flex flex-wrap gap-2">
                <button 
                  className="btn btn-secondary" 
                  onClick={handleBackToSales}
                  disabled={isSubmitting}
                >
                  Back to Sale
                </button>
                <button 
                  className="btn btn-success" 
                  onClick={handleConfirmSale}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Processing...' : 'Confirm Sale'}
                </button>
              </div>

            </div>
          </div>
        </div>

        {/* RECEIPT */}
        <div className="col-12 col-lg-6">
            <div
            id="receipt-print"
            ref={receiptRef}
            className="card shadow-sm border-0 h-100 d-flex"
              style={{
                borderRadius: '18px',
                background: '#ffffff',
                padding: '0.75rem 1.75rem 0.75rem 1.25rem',
                fontFamily: "Arial, Helvetica, 'Helvetica Neue', sans-serif",
                fontSize: '11px',
                lineHeight: '1.3',
                fontWeight: '700',
              }}
            >
            {!hasCartItems ? (
              <div className="text-center text-muted my-auto">
                <p className="mb-0">Receipt preview will appear once items are added to the cart.</p>
              </div>
            ) : (
              <>
              <div className="text-center mb-3">
                <h5 className="fw-bold m-0">Decor Masters</h5>
                {splitBranchAddressForReceipt(branchAddress, 56).map((line, idx) => (
                  <p key={`addr-${idx}`} className="small m-0">{line}</p>
                ))}
                {receiptHeaderPhoneLine ? (
                  <p className="small m-0">{receiptHeaderPhoneLine}</p>
                ) : null}
                <hr />
              </div>

              <div className="d-flex justify-content-between small">
                  <span>Branch:</span>
                  <span>{branchName}</span>
                </div>
                <div className="d-flex justify-content-between small">
                  <span>Cashier:</span>
                  <span>{cashierName}</span>
                </div>
                <div className="d-flex justify-content-between small mt-2">
                <span>Receipt No:</span>
                <span>{receiptNumber}</span>
              </div>
              <div className="d-flex justify-content-between small mb-3">
                <span>Date:</span>
                <span>{new Date().toLocaleString()}</span>
              </div>

              <h6 className="fw-bold">Customer: {customerName}</h6>
              <hr />

              <h6 className="fw-bold">Items</h6>
              {cartItems.map((item, index) => (
                <div key={index} className="d-flex justify-content-between small">
                  <span>
                    {item.name} x{item.quantity}
                  </span>
                  <span>{currencySymbol} {convertForDisplay(item.price * item.quantity).toFixed(2)}</span>
                </div>
              ))}

              <hr />
              <div className="d-flex justify-content-between fw-bold">
                <span>Subtotal</span>
                <span>{currencySymbol} {convertForDisplay(subtotal).toFixed(2)}</span>
              </div>

              {calculatedDiscount > 0 && selectedDiscount && (
                <div className="d-flex justify-content-between mt-2 small">
                  <span>Discount ({selectedDiscount.name})</span>
                  <span className="text-success">-{currencySymbol} {convertForDisplay(calculatedDiscount).toFixed(2)}</span>
                </div>
              )}

              <div className="d-flex justify-content-between mt-2 fw-bold">
                <span>Total</span>
                <span style={isZig ? { color: '#ff6b00', fontWeight: 'bold' } : {}}>
                  {currencySymbol} {total.toFixed(2)}
                </span>
              </div>

              <div className="d-flex justify-content-between mt-2 small">
                <span>Payment Method</span>
                <span style={isZig ? { color: '#ff6b00', fontWeight: 'bold' } : {}}>
                  {formatPaymentMethod(paymentMethod)}
                </span>
              </div>

              <div className="d-flex justify-content-between mt-2 small">
                <span>Amount Received</span>
                <span style={isZig ? { color: '#ff6b00', fontWeight: 'bold' } : {}}>
                  {currencySymbol} {formattedAmountReceived}
                </span>
              </div>

              <div className="d-flex justify-content-between mt-2 fw-bold text-success">
                <span>Change</span>
                <span style={isZig ? { color: '#ff6b00', fontWeight: 'bold' } : {}}>
                  {currencySymbol} {changeDue.toFixed(2)}
                </span>
              </div>

              <hr />

              <p className="text-center small mt-3">Thank you for choosing Wealthbag Logistics!</p>
              </>
            )}
            </div>
        </div>

      </div>
    </section>
  )
}

export default Checkout

